import { id } from "@suite/core";

/** Artwork that can be stamped onto pages: a drawn/typed/uploaded signature. */
export interface SavedSignature {
  id: string;
  png: Uint8Array;
  /** The same bytes as a data URL, for <img> previews and persistence. */
  dataUrl: string;
  /** width / height */
  aspect: number;
  createdAt: number;
}

const KEY = "pdf-tools:signatures";
const MAX_SAVED = 6;

export function loadSignatures(): SavedSignature[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Omit<SavedSignature, "png">[];
    return parsed
      .filter((s) => typeof s.dataUrl === "string" && s.dataUrl.startsWith("data:image/png"))
      .map((s) => ({ ...s, png: dataUrlToBytes(s.dataUrl) }));
  } catch {
    return [];
  }
}

export function persistSignatures(list: SavedSignature[]): void {
  try {
    const slim = list.slice(0, MAX_SAVED).map(({ id, dataUrl, aspect, createdAt }) => ({ id, dataUrl, aspect, createdAt }));
    localStorage.setItem(KEY, JSON.stringify(slim));
  } catch {
    // Quota or private mode: the signature still works for this session.
  }
}

export function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}

/** Turns a canvas with transparent surroundings into a saved signature, trimmed to its ink. */
export function signatureFromCanvas(canvas: HTMLCanvasElement): SavedSignature | null {
  const trimmed = trimTransparent(canvas, 12);
  if (!trimmed) return null;
  const dataUrl = trimmed.toDataURL("image/png");
  return {
    id: id("sig"),
    png: dataUrlToBytes(dataUrl),
    dataUrl,
    aspect: trimmed.width / trimmed.height,
    createdAt: Date.now(),
  };
}

/**
 * Renders typed text to a signature image. Waits for the webfont so the canvas
 * doesn't fall back to a system face on first use.
 */
export async function signatureFromText(
  text: string,
  family: string,
  weight: string | number = 400,
): Promise<SavedSignature | null> {
  const value = text.trim();
  if (!value) return null;
  const scale = 4;
  const size = 40 * scale;
  const font = `${weight} ${size}px ${family}`;
  try {
    await document.fonts.load(font, value);
  } catch {
    // No FontFaceSet (very old browser) — draw with whatever is available.
  }
  const probe = document.createElement("canvas").getContext("2d")!;
  probe.font = font;
  const metrics = probe.measureText(value);
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(metrics.width + size);
  canvas.height = Math.ceil(size * 1.6);
  const ctx = canvas.getContext("2d")!;
  ctx.font = font;
  ctx.fillStyle = "#1c2a5e";
  ctx.textBaseline = "middle";
  ctx.fillText(value, size / 2, canvas.height / 2);
  return signatureFromCanvas(canvas);
}

/**
 * Loads an uploaded image. With `removeWhite`, near-white pixels become
 * transparent so a photo or scan of a signature sits on the page without a
 * grey box around it.
 */
export async function signatureFromImage(file: File, removeWhite: boolean): Promise<SavedSignature | null> {
  const bitmap = await createImageBitmap(file);
  const maxEdge = 1600;
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  if (removeWhite) {
    const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const px = image.data;
    for (let i = 0; i < px.length; i += 4) {
      const luma = 0.299 * px[i]! + 0.587 * px[i + 1]! + 0.114 * px[i + 2]!;
      // Full transparency above 235, fading in over the band below so anti-
      // aliased strokes keep soft edges instead of a hard cut-out.
      if (luma > 235) px[i + 3] = 0;
      else if (luma > 180) px[i + 3] = Math.round((px[i + 3]! * (235 - luma)) / 55);
    }
    ctx.putImageData(image, 0, 0);
  }
  return signatureFromCanvas(canvas);
}

function trimTransparent(source: HTMLCanvasElement, pad: number): HTMLCanvasElement | null {
  const ctx = source.getContext("2d")!;
  const { data } = ctx.getImageData(0, 0, source.width, source.height);
  let minX = source.width, minY = source.height, maxX = -1, maxY = -1;
  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      if (data[(y * source.width + x) * 4 + 3]! > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  const out = document.createElement("canvas");
  out.width = maxX - minX + 1 + pad * 2;
  out.height = maxY - minY + 1 + pad * 2;
  out.getContext("2d")!.drawImage(source, minX - pad, minY - pad, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}
